import {
  useConfiguratorField,
  type FieldProps,
} from '@eliadmin/core/src/components/configurator/configurator-field';
import { ConfiguratorFieldLabel } from '@eliadmin/core/src/components/configurator/configurator-field-label';
import { ConfiguratorImagePreview } from '@eliadmin/core/src/components/configurator/configurator-image-preview';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  CustomDialog,
  CustomDialogContent,
  CustomDialogFooter,
  CustomDialogTrigger,
} from '@eliadmin/core/src/components/ui/custom-dialog';
import { Highlight } from '@eliadmin/core/src/components/ui/highlight';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import { VirtualList } from '@eliadmin/core/src/components/ui/virtual-list';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import type { FieldDataItem } from '@eliadmin/core/src/stores/configurator-store';
import {
  useConfiguratorStore,
  useErrSync,
  useSetValueMutationPending,
} from '@eliadmin/core/src/stores/configurator-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { LuChevronDown, LuX } from 'react-icons/lu';
import { RiSearch2Line } from 'react-icons/ri';
import {
  getConfirmedConfiguratorSelection,
  getConfiguratorSelectionSaveError,
} from './configurator-select-state';

const DEBUG_CONFIGURATOR = true;

function debugConfiguratorSelect(message: string, payload?: any) {
  if (!DEBUG_CONFIGURATOR) return;
  try {
    console.log(`[ConfiguratorSelect] ${message}`, payload ?? '');
  } catch {
    // no-op
  }
}

class ConfiguratorDialogErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: any }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: undefined };
  }
  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }
  componentDidCatch(error: any, errorInfo: any) {
    try {
      Sentry.captureException(error, { extra: { errorInfo, component: 'ConfiguratorSelect' } });
    } catch {
      // no-op
    }
    // Also log to console so it is visible in production devtools

    console.error('ConfiguratorSelect: dialog crashed', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col gap-2 p-4">
          <span className="text-sm text-destructive">Došlo k chybe pri otvorení výberu.</span>
          <span className="break-all text-xs text-muted-foreground">
            {(this.state.error && (this.state.error.message || String(this.state.error))) ||
              'Unknown error'}
          </span>
        </div>
      );
    }
    return this.props.children as any;
  }
}

/**
 * normalize stirng for search remove any special characters
 */
function normalizeString(str: string) {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export function ConfiguratorSelect({
  fieldConfig,
  field,
  onClear,
  isDisabled,
  isClearable = true,
}: FieldProps<'select'> & {
  onClear?: () => void;
  isDisabled?: boolean;
  isClearable?: boolean;
}) {
  const { isOpen, setIsOpen } = useDisclosure();
  const isSaving = useSetValueMutationPending();
  const locale = useLocaleStore(state => state.currentLocale);

  const [currentSelection, setCurrentSelection] = useState<FieldDataItem | null>(null);

  const {
    fieldData: fieldDataOrg,
    value,
    onChange,
    productConfId,
    mutationError,
  } = useConfiguratorField(fieldConfig);

  const productConf = useConfiguratorStore(state => state.productConf);

  const fieldData = useMemo(() => {
    if (!field.getFieldData) {
      return fieldDataOrg ?? [];
    }

    try {
      if (!productConfId || !productConf) {
        debugConfiguratorSelect('getFieldData skipped, missing productConf/productConfId', {
          column: fieldConfig.column,
          fieldId: field.id,
          hasProductConfId: !!productConfId,
          hasProductConf: !!productConf,
        });
        return fieldDataOrg ?? [];
      }

      const data = field.getFieldData({
        fieldProps: { field, fieldConfig },
        productConfId,
        value,
        productConf,
      });
      debugConfiguratorSelect('getFieldData success', {
        column: fieldConfig.column,
        fieldId: field.id,
        value,
        length: Array.isArray(data) ? data.length : undefined,
      });
      return data;
    } catch (error) {
      try {
        Sentry.captureException(error, {
          extra: { where: 'field.getFieldData', fieldConfig, productConfId, value },
        });
      } catch {
        // no-op
      }
      debugConfiguratorSelect('getFieldData error', {
        column: fieldConfig.column,
        fieldId: field.id,
        // Best-effort stringify
        error: (error as any)?.message || (typeof error === 'string' ? error : String(error)),
      });
      // Also log to console so it is visible in production devtools

      console.error('ConfiguratorSelect: field.getFieldData error', error, {
        fieldConfig,
        productConfId,
        value,
      });
      return fieldDataOrg ?? [];
    }
  }, [field, fieldConfig, fieldDataOrg, productConf, productConfId, value]);

  const selected =
    (Array.isArray(fieldData) ? fieldData.find(item => item && item.kod === value) : null) ?? null;

  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);

  const filteredData = useMemo(() => {
    debugConfiguratorSelect('filter start', {
      column: fieldConfig.column,
      fieldId: field.id,
      value,
      query: deferredQuery,
      fieldDataLength: fieldData?.length ?? 0,
    });

    const baseData = Array.isArray(fieldData) ? fieldData : [];

    let result: any[] =
      field.filterFn?.(baseData, {
        fieldProps: { field, fieldConfig },
        productConfId,
        value,
        productConf,
      }) ?? baseData;

    // guard against nullish/invalid items
    result = (result as any[]).filter(
      (item: any) => item && typeof item.kod === 'string' && typeof item.popis === 'string',
    );

    result =
      (result ?? []).filter(item => {
        const lookupString = `${item.kod} ${item.popis}`;
        return normalizeString(lookupString).includes(normalizeString(deferredQuery));
      }) ?? [];

    // if field.orderBy is set, sort the data

    if (field.orderBy) {
      result = [...result].sort(field.orderBy);
    }

    debugConfiguratorSelect('filter done', {
      column: fieldConfig.column,
      fieldId: field.id,
      resultLength: result.length,
    });

    return result;
  }, [fieldData, field, fieldConfig, productConfId, value, productConf, deferredQuery]);

  const isInvalid = !selected && fieldConfig.must === '1';
  useErrSync(fieldConfig.column, isInvalid);

  const getValueLabel = (item?: FieldDataItem) => {
    return field.renderValueLabel
      ? field.renderValueLabel(item)
      : item
        ? `${item.kod} ${item.popis}`
        : '';
  };

  const closeDialog = (shouldSave: boolean = false) => {
    if (shouldSave && isSaving) return;
    setIsOpen(false);
    if (shouldSave) {
      const nextValue = getConfirmedConfiguratorSelection(value, currentSelection);
      if (nextValue !== value) {
        debugConfiguratorSelect('closeDialog: apply selection', {
          column: fieldConfig.column,
          fieldId: field.id,
          prevValue: value,
          nextValue,
        });
        onChange(nextValue);
      } else {
        debugConfiguratorSelect('closeDialog: selection unchanged, skipping onChange', {
          column: fieldConfig.column,
          fieldId: field.id,
          value,
        });
      }
    } else {
      debugConfiguratorSelect('closeDialog: cancel', {
        column: fieldConfig.column,
        fieldId: field.id,
        value,
      });
    }
    setCurrentSelection(null);
  };

  debugConfiguratorSelect('render', {
    column: fieldConfig.column,
    fieldId: field.id,
    value,
    isOpen,
    hasSelection: !!currentSelection,
  });

  // Clear transient UI state whenever dialog closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentSelection(null);
      setQuery('');
      debugConfiguratorSelect('dialog closed: cleared transient state', {
        column: fieldConfig.column,
        fieldId: field.id,
      });
    }
  }, [isOpen, fieldConfig.column, field.id]);

  return (
    <div className="flex w-full flex-col gap-1">
      <ConfiguratorFieldLabel fieldConfig={fieldConfig} isInvalid={isInvalid} />
      <CustomDialog
        open={isOpen}
        // Let Radix drive the next open state, we just mirror it in local state.
        // React will ignore no-op updates, so we don't need to guard.
        onOpenChange={setIsOpen}
      >
        <CustomDialogTrigger
          asChild
          className="flex flex-col gap-1 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={isDisabled || isSaving || !fieldData}
        >
          <Input
            disabled={isDisabled || isSaving || !fieldData}
            type="select"
            variant="outlined"
            id={fieldConfig?.column}
            className={{ main: 'w-full cursor-pointer placeholder:text-accent' }}
            readOnly
            after={
              value && isClearable ? (
                <Button
                  size={'icon'}
                  variant="ghost"
                  type="button"
                  disabled={isSaving}
                  className="-mr-2 text-destructive hover:bg-destructive/20"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isSaving) return;
                    onChange('');
                    onClear?.();
                  }}
                >
                  <LuX />
                </Button>
              ) : (
                <LuChevronDown />
              )
            }
            value={getValueLabel(selected)}
            aria-invalid={isInvalid}
            before={
              selected?.pic ? (
                <Image
                  src={`https://apieshop.smako.cz/files/data/${selected.pic}`}
                  alt={selected.popis}
                  width={24}
                  height={24}
                  className={'h-6 w-6 border-accent object-contain text-accent focus:bg-accent'}
                />
              ) : selected?.html ? (
                <div
                  className="h-6 w-6 border-accent text-accent focus:bg-accent"
                  style={{ background: selected.html }}
                />
              ) : null
            }
          />
        </CustomDialogTrigger>
        {isInvalid && <span className="text-sm text-destructive">Tento údaj je povinný</span>}
        {mutationError && (
          <span role="alert" className="text-sm text-destructive">
            {getConfiguratorSelectionSaveError(locale, mutationError)}
          </span>
        )}
        <CustomDialogContent className="flex h-[98vh] flex-col">
          <ConfiguratorDialogErrorBoundary>
            <div className="absolute right-0 top-0 md:right-2 md:top-2">
              <Button
                size="icon"
                variant="ghost"
                className="md:h-12 md:w-12"
                onClick={() => closeDialog(false)}
              >
                <LuX className="h-6 w-6" />
              </Button>
            </div>
            <div className=" w-full px-4 md:px-0">
              <Input
                value={query}
                variant={'outlined'}
                onChange={e => setQuery(e.target.value)}
                placeholder="Vyhľadávanie"
                className={{ wrapper: 'flex-1' }}
                before={<RiSearch2Line className="h-6 w-6 text-muted-foreground" />}
              />
            </div>
            <RadioGroup
              id={fieldConfig.column}
              className="flex h-[80%] w-full flex-col overflow-hidden"
              value={currentSelection?.kod ?? value}
              onValueChange={(kod: string) => {
                const next = fieldData?.find(item => item.kod === kod) ?? null;
                debugConfiguratorSelect('onValueChange', {
                  column: fieldConfig.column,
                  fieldId: field.id,
                  kod,
                  found: !!next,
                });
                setCurrentSelection(next);
              }}
            >
              <VirtualList
                data={filteredData ?? []}
                estimatedItemSize={108}
                keyExtractor={item => String(item.kod)}
                className={{ main: 'h-full' }}
                renderItem={item => {
                  // return <span>{item.kod}</span>;
                  return (
                    <Label
                      className="flex w-full cursor-pointer items-center justify-start gap-4 rounded-lg px-4 py-2 transition hover:bg-muted"
                      htmlFor={String(item.kod)}
                    >
                      <RadioGroupItem
                        value={String(item.kod)}
                        id={String(item.kod)}
                        className="h-6 w-6 border-accent text-accent data-[state=checked]:bg-accent"
                      />
                      <ConfiguratorImagePreview item={item} />
                      <Highlight
                        className={{
                          root: 'text-sm font-normal text-muted-foreground sm:text-base',
                          highlight: 'text-accent',
                        }}
                        query={deferredQuery}
                      >
                        {getValueLabel(item)}
                      </Highlight>
                    </Label>
                  );
                }}
              />
            </RadioGroup>
            <CustomDialogFooter className="fixed bottom-0 left-0 w-full border-t border-t-muted bg-background px-4 py-4 sm:px-16">
              <Button
                variant={'accent'}
                className="w-full rounded-full text-base font-medium sm:w-fit"
                type="button"
                disabled={isSaving}
                onClick={() => closeDialog(true)}
              >
                Potvrdiť výber
              </Button>
            </CustomDialogFooter>
          </ConfiguratorDialogErrorBoundary>
        </CustomDialogContent>
      </CustomDialog>
    </div>
  );
}
